"use client";

import React from "react";
import { PRIVATE_DINING_SALONS, PrivateDiningSalon } from "@/lib/demos/restaurantData";
import { Wine, Users, Sparkles, Check, ArrowRight, Shield } from "lucide-react";

interface PrivateDiningSectionProps {
  currency: "INR" | "USD";
  onSelectSalon: (salonId: string) => void;
}

export default function PrivateDiningSection({
  currency,
  onSelectSalon,
}: PrivateDiningSectionProps) {
  return (
    <section className="py-16 bg-stone-950 border-b border-stone-800 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Wine className="w-3.5 h-3.5" />
              <span>Private Asset Dining & Salons</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Bespoke Salons & VIP Enclaves
            </h2>
            <p className="text-sm sm:text-base text-stone-300 max-w-2xl">
              From subterranean limestone wine cellars to rooftop panoramic glass conservatories, host your corporate milestones, board celebrations, and private tastings in unparalleled exclusivity.
            </p>
          </div>
        </div>

        {/* Salons Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {PRIVATE_DINING_SALONS.map((salon: PrivateDiningSalon) => {
            const minSpend = currency === "INR" ? salon.minSpendINR : salon.minSpendUSD;

            return (
              <div
                key={salon.id}
                className="bg-stone-900/80 rounded-2xl border border-stone-800 hover:border-amber-500/40 transition-all duration-300 flex flex-col overflow-hidden group shadow-xl hover:-translate-y-1"
              >
                <div className="relative h-56 overflow-hidden bg-stone-950">
                  <img
                    src={salon.image}
                    alt={salon.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 brightness-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/30 to-transparent" />
                  
                  <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md px-3 py-1 rounded-full border border-amber-500/30 text-xs font-bold text-amber-300 flex items-center gap-1.5">
                    <Users className="w-3 h-3" />
                    <span>{salon.capacity}</span>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
                    <span className="text-xs font-bold text-emerald-400 bg-black/60 px-2.5 py-1 rounded backdrop-blur-md border border-emerald-500/30">
                      {minSpend}
                    </span>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold text-white group-hover:text-amber-300 transition-colors">
                      {salon.name}
                    </h3>
                    <p className="text-xs text-stone-300 leading-relaxed">
                      {salon.ambience}
                    </p>

                    {/* Features */}
                    <div className="pt-2 space-y-2 border-t border-stone-800/80">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400/90 block">
                        Included Salon Privileges:
                      </span>
                      <ul className="space-y-1.5">
                        {salon.features.map((feat, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-xs text-stone-300">
                            <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <button
                    onClick={() => onSelectSalon(salon.id)}
                    className="w-full py-2.5 rounded-xl bg-stone-950 hover:bg-stone-800 border border-stone-700 hover:border-amber-500/50 text-white font-semibold text-xs transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    <span>Inquire for {salon.name}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-amber-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
